import type { TableFilterState, TableSortingState } from '@ibsys/kit';
import { useCallback } from 'react';
import type { KnowledgeSourceListItem } from '@/entities/knowledgeSources';
import { type SearchKnowledgeSourceRequest, useLazyKnowledgeSourcesSearch } from '@/entities/search';
import { buildSearchQueryParams, buildSearchRequest } from '@/features/smart-search';

type FetchParams = {
  limit: number;
  offset: number;
  filters?: TableFilterState;
  sorting?: TableSortingState;
};

/**
 * Источники отбираются только собственными колонками — запроса пользователя в срезе нет:
 * поискового предиката для них не существует, и сервер его отклоняет.
 */
const NO_QUERY = buildSearchQueryParams(null);

/**
 * Загрузка порции списка источников знаний: состояние списка (условия отбора, порядок,
 * постраничность) переводится в запрос теми же общими правилами, что и у остальных списков.
 */
export const useKnowledgeSourcesFetch = () => {
  const { refetch } = useLazyKnowledgeSourcesSearch();

  return useCallback(
    async ({
      limit,
      offset,
      filters = [],
      sorting = [],
    }: FetchParams): Promise<{ total: number; items: KnowledgeSourceListItem[] }> => {
      const request = buildSearchRequest('knowledge_source', NO_QUERY, {
        limit,
        offset,
        filters,
        sorting,
      }) as SearchKnowledgeSourceRequest;

      const result = await refetch(request);
      return { total: result.total, items: result.items };
    },
    [refetch],
  );
};
